import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { IconExternalLink } from "@tabler/icons-react";

/** Where a source opens: in-app for a Juisci article, out for everything else. */
export type SourceTarget =
  { kind: "internal"; path: string } | { kind: "external"; url: string };

/**
 * The way out to the source itself: the article in the app, the page on the web.
 *
 * One mark for every sheet that states a source — the chat's and Evidence's —
 * so the way out of a paper is in the same place, at the same weight, whichever
 * route the reader arrived by.
 */
export function SourceLink({
  target,
}: {
  target: SourceTarget;
}): React.JSX.Element {
  const { t } = useTranslation();
  const icon = (
    <IconExternalLink stroke={1.5} className="size-6 text-ds-mint-800" />
  );
  switch (target.kind) {
    case "internal":
      return (
        <Link
          to={target.path}
          aria-label={t("Open the source")}
          className="shrink-0 self-end no-underline"
        >
          {icon}
        </Link>
      );
    case "external":
      return (
        <a
          href={target.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("Open the source")}
          className="shrink-0 self-end no-underline"
        >
          {icon}
        </a>
      );
    default:
      throw new Error(target satisfies never);
  }
}
